import { isEmail } from 'validator';

import {
  addressProperties,
  addressRequired,
  customValidate
} from 'common/components/address/validators';
import {
  isDayMonthYearADate,
  validateMinimumAge,
  isValidBirthDate
} from 'common/helpers/dateHelper';
import { validateForUI } from 'common/helpers/jsonValidator';
import { getMinimumAge } from 'common/helpers/miscHelper';
import { isValidPhoneNumber } from 'common/helpers/phoneHelper';

const loggedInCustomer = () => {
  const schema = {
    type: 'object',
    properties: addressProperties,
    required: addressRequired,
    additionalProperties: false
  };
  schema.properties.customerAddressId = { type: 'string' };
  schema.properties.giftMessage = { type: 'string' };
  schema.properties.shippingInstructions = { type: 'string' };
  return schema;
};

const notLoggedInCustomer = () => {
  const schema = {
    type: 'object',
    properties: addressProperties,
    required: [...addressRequired, 'email'],
    additionalProperties: false
  };
  schema.properties.email = { type: 'string' };
  schema.properties.isSubscribe = { type: 'boolean' };
  schema.properties.giftMessage = { type: 'string' };
  schema.properties.shippingInstructions = { type: 'string' };
  return schema;
};

const loggedInCustomerPickup = {
  type: 'object',
  properties: {
    firstName: { type: 'string' },
    lastName: { type: 'string' },
    birthDay: { type: 'integer' },
    birthMonth: { type: 'integer' },
    birthYear: { type: 'integer' },
    phone: { type: 'string' },
    countryCode: { type: 'string', maxLength: 2, minLength: 2 },
    inventoryLocationId: { type: 'string', format: 'uuid' }
  },
  required: [
    'firstName',
    'lastName',
    'birthDay',
    'birthMonth',
    'birthYear',
    'phone',
    'countryCode',
    'inventoryLocationId'
  ],
  additionalProperties: false
};

const notLoggedInCustomerPickup = {
  type: 'object',
  properties: {
    firstName: { type: 'string' },
    lastName: { type: 'string' },
    birthDay: { type: 'integer' },
    birthMonth: { type: 'integer' },
    birthYear: { type: 'integer' },
    phone: { type: 'string' },
    countryCode: { type: 'string', maxLength: 2, minLength: 2 },
    email: { type: 'string' },
    isSubscribe: { type: 'boolean' },
    inventoryLocationId: { type: 'string', format: 'uuid' }
  },
  required: [
    'firstName',
    'lastName',
    'birthDay',
    'birthMonth',
    'birthYear',
    'phone',
    'countryCode',
    'email',
    'inventoryLocationId'
  ],
  additionalProperties: false
};

export const validateForLoggedInCustomerShip = (address, settings) => {
  const errors = validateForUI(
    loggedInCustomer(),
    address,
    customValidate(address, 'shipping', settings)
  );
  return errors;
};

export const validateForLoggedInCustomerPickup = (address, settings) => {
  const errors = validateForUI(
    loggedInCustomerPickup,
    address,
    customValidatePickup(address, settings)
  );
  return errors;
};

export const validateForNotLoggedInCustomerShip = (address, settings) => {
  const errors = validateForUI(
    notLoggedInCustomer(),
    address,
    customValidateNotLoggedInShip(address, settings)
  );
  return errors;
};

export const validateForNotLoggedInCustomerPickup = (address, settings) => {
  const errors = validateForUI(
    notLoggedInCustomerPickup,
    address,
    customValidatePickup(address, settings)
  );
  return errors;
};

const customValidateNotLoggedInShip = (address, settings) => {
  const errorArray = customValidate(address, 'shipping', settings);
  if (address.email && !isEmail(address.email)) {
    const error = { field: `email`, message: 'Invalid Email' };
    errorArray.push(error);
  }
  return errorArray;
};

const customValidatePickup = (address, settings) => {
  const errorArray = [];

  if (address.birthDay && (address.birthDay < 1 || address.birthDay > 31)) {
    const error = { field: 'birthDay', message: 'Invalid Day' };
    errorArray.push(error);
  }
  if (
    address.birthMonth &&
    (address.birthMonth < 1 || address.birthMonth > 12)
  ) {
    const error = { field: 'birthMonth', message: 'Invalid Month' };
    errorArray.push(error);
  }
  if (
    address.birthYear &&
    (address.birthYear < 1900 || address.birthYear > 2020)
  ) {
    const error = { field: 'birthYear', message: 'Invalid Year' };
    errorArray.push(error);
  }
  if (address.birthDay && address.birthMonth && address.birthYear) {
    if (
      !isDayMonthYearADate(
        address.birthDay,
        address.birthMonth,
        address.birthYear
      )
    ) {
      const error = { field: 'birthDay', message: 'Invalid Day' };
      errorArray.push(error);
    }

    const isMinimumAge = validateMinimumAge(
      address.birthDay,
      address.birthMonth,
      address.birthYear,
      settings
    );

    if (!isMinimumAge) {
      const minAge = getMinimumAge(settings);
      const error = {
        field: 'birthYear',
        message: `Must be at least ${minAge} years old`
      };
      errorArray.push(error);
    }

    if (!isValidBirthDate(address.birthDay, address.month, address.birthYear)) {
      const error = {
        field: 'birthYear',
        message: `Invalid birth date`
      };
      errorArray.push(error);
    }
  }

  if (
    address.phone &&
    !isValidPhoneNumber(address.phone, address.countryCode, settings)
  ) {
    errorArray.push({
      field: 'phone',
      message: 'Invalid phone number for your country code'
    });
  }

  if (address.email && !isEmail(address.email)) {
    const error = { field: `email`, message: 'Invalid Email' };
    errorArray.push(error);
  }
  return errorArray;
};
